import { useRef } from "react";
import useSWR from "swr";

import { getDirectoryListings } from "common/middleware/api.properties";
import { PaginatedResultOfListingLookupDto } from "common/middleware/api.services";

const DIRECTORY_LISTINGS_KEY = "directoryListings";

interface UseDirectoryListingsOptions {
  page?: number;
  pageSize?: number;
  searchString?: string;
  sortOrder?: string;
  ascSortDirection?: boolean;
  minLatitude?: number | null;
  maxLatitude?: number | null;
  minLongitude?: number | null;
  maxLongitude?: number | null;
  shouldFetch?: boolean;
}

export const useDirectoryListings = (options: UseDirectoryListingsOptions = {}) => {
  const {
    page = 0,
    pageSize = 20,
    searchString,
    sortOrder,
    ascSortDirection = false,
    minLatitude,
    maxLatitude,
    minLongitude,
    maxLongitude,
    shouldFetch = true,
  } = options;

  const previousDataRef = useRef<PaginatedResultOfListingLookupDto>();

  const { data, error } = useSWR(
    shouldFetch
      ? [
          DIRECTORY_LISTINGS_KEY,
          page,
          pageSize,
          searchString,
          sortOrder,
          ascSortDirection,
          minLatitude,
          maxLatitude,
          minLongitude,
          maxLongitude,
        ]
      : null,
    getDirectoryListings,
  );

  // Keep previous data visible while new data loads to prevent marker flickering
  if (data) {
    previousDataRef.current = data;
  }

  const displayData = data ?? previousDataRef.current;

  return {
    listings: displayData?.entities,
    totalCount: displayData?.count ?? 0,
    totalPages: displayData?.totalPages ?? 0,
    isLoading: !error && !data,
    isError: error,
  };
};
